import {
	ApiUser,
	isAdminUser,
	isEmployeeUser,
	isStudentUser,
} from 'auth/token';

import {
	CommunityOfferListDto,
	CommunityOfferType,
} from 'typing/communityOffers';

export type CommunityOfferCapabilities = {
	view: boolean;
	create: boolean;
	edit: boolean;
	allowedTypes: CommunityOfferType[];
};

export const getCommunityOfferCapabilities = (
	user?: ApiUser,
): CommunityOfferCapabilities => {
	if (isAdminUser(user) || isEmployeeUser(user)) {
		return {
			view: true,
			create: true,
			edit: true,
			allowedTypes: ['BRIGADE', 'TUTORING'],
		};
	}
	if (isStudentUser(user)) {
		return {
			view: true,
			create: true,
			edit: true,
			allowedTypes: ['TUTORING'],
		};
	}
	return { view: false, create: false, edit: false, allowedTypes: [] };
};

export const canEditCommunityOffer = (
	user: ApiUser | undefined,
	offer: Pick<CommunityOfferListDto, 'author' | 'type' | 'isVisible'>,
) =>
	isAdminUser(user) ||
	(user?.id === offer.author.id &&
		(isEmployeeUser(user) ||
			(isStudentUser(user) && offer.type === 'TUTORING' && !offer.isVisible)));

export const canActivateCommunityOffer = (
	user: ApiUser | undefined,
	authorId: string,
	isVisible: boolean,
) =>
	!isVisible &&
	(isEmployeeUser(user) ? user.id === authorId : isAdminUser(user));

export const canDeactivateCommunityOffer = (
	user: ApiUser | undefined,
	authorId: string,
	isVisible: boolean,
) =>
	isVisible &&
	(isAdminUser(user) ||
		((isEmployeeUser(user) || isStudentUser(user)) && user?.id === authorId));

export const canCreateCommunityOfferResponse = (
	user: ApiUser | undefined,
	offer: Pick<
		CommunityOfferListDto,
		'author' | 'type' | 'isVisible' | 'validFrom' | 'validTo'
	>,
	today?: string,
) => {
	const now = new Date();
	const localToday =
		today ??
		`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
			2,
			'0',
		)}-${String(now.getDate()).padStart(2, '0')}`;
	return (
		(isStudentUser(user) ||
			(isEmployeeUser(user) && offer.type === 'TUTORING')) &&
		user?.id !== offer.author.id &&
		offer.isVisible === true &&
		!!offer.validFrom &&
		!!offer.validTo &&
		offer.validFrom <= localToday &&
		localToday <= offer.validTo
	);
};

export const canViewOwnCommunityOfferResponses = (user?: ApiUser) =>
	isStudentUser(user) || isEmployeeUser(user);

export const canViewOwnCommunityOfferTopicSuggestions = (user?: ApiUser) => {
	const capabilities = getCommunityOfferCapabilities(user);
	return capabilities.create && capabilities.allowedTypes.includes('TUTORING');
};

export const canViewCommunityOfferResponsesRoute = (user?: ApiUser) =>
	isAdminUser(user) || isEmployeeUser(user) || isStudentUser(user);

export const canViewCommunityOfferResponses = (
	user: ApiUser | undefined,
	authorId: string,
) =>
	isAdminUser(user) ||
	((isEmployeeUser(user) || isStudentUser(user)) && user?.id === authorId);
